import { useEffect, useState, useCallback } from 'react';
import { db, auth } from "../firebase";
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { 
  getSpaceTasks, createSpaceTask, updateSpaceTask, deleteSpaceTask 
} from './services/spaceTasksService';
import { 
  getSpaceGroups, createSpaceGroup, updateSpaceGroup, deleteSpaceGroupAndTasks 
} from './services/spaceGroupsService';
import { doc, getDoc } from 'firebase/firestore';
import './SpaceApp.css'; 
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, onSnapshot,
  serverTimestamp, 
  updateDoc 
} from 'firebase/firestore';

function SpaceMain() {
  const { spaceId, username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // --- States ---
  const [tasks, setTasks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [spaceData, setSpaceData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [editingTask, setEditingTask] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [statusFilter, setStatusFilter] = useState('all');
  const [easeFilter, setEaseFilter] = useState('all');
  const [hoverRating, setHoverRating] = useState(0);

  const initialForm = {
  title: '', 
  notes: '', 
  importance: 5,
  dueDate: `${new Date().toISOString().split('T')[0]}T23:59`,
  group: '', 
  difficulty: 'Medium', // 🌟 Default value
  status: 'todo',      // 🌟 Default value
  assignedToEmail: ''
};
  const [form, setForm] = useState(initialForm);
  const [groupForm, setGroupForm] = useState({ name: '', notes: '', priority: 5, parentId: null });
  // 🌟 Fetch Logic
  const fetchAllSpaceData = useCallback(async () => {
    if (!spaceId) return;
    try {
      const sDoc = await getDoc(doc(db, "spaces", spaceId));
      setSpaceData(sDoc.data());

      const [taskData, groupData] = await Promise.all([
        getSpaceTasks(spaceId),
        getSpaceGroups(spaceId)
      ]);
      setTasks(taskData);
      setGroups(groupData);
    } catch (err) {
      console.error("Fetch Error:", err);
    }
  }, [spaceId]);

 // --- Real-time Sync ---
  useEffect(() => {
    if (!user || !spaceId) return;
    
    // Sync Space Metadata
    const unsubSpace = onSnapshot(doc(db, "spaces", spaceId), (d) => setSpaceData(d.data()));
    
    // Sync Space Tasks
    const qTasks = query(collection(db, "space_tasks"), where("spaceId", "==", spaceId));
    const unsubTasks = onSnapshot(qTasks, (s) => 
      setTasks(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    // Sync Space Groups
    const qGroups = query(collection(db, "space_groups"), where("spaceId", "==", spaceId));
    const unsubGroups = onSnapshot(qGroups, (s) => 
      setGroups(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    return () => { unsubSpace(); unsubTasks(); unsubGroups(); };
  }, [user, spaceId]);
  // 🌟 Tree Logic for Subgroups
const buildGroupTree = (flatGroups) => {
    const map = {};
    const tree = [];
    flatGroups.forEach(g => map[g.id] = { ...g, children: [] });
    flatGroups.forEach(g => {
      if (g.parentId && map[g.parentId]) map[g.parentId].children.push(map[g.id]);
      else tree.push(map[g.id]);
    });
    return tree;
  };

  // 🌟 Handlers
  // 3. Updated Task Form Handler
const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    
    const payload = { 
      ...form, 
      spaceId, 
      updatedAt: serverTimestamp(),
      assignedToEmail: form.assignedToEmail || user.email 
    };

    if (editingTask) {
      await updateSpaceTask(editingTask.id, payload);
    } else {
      await createSpaceTask(spaceId, payload);
    }
    setForm(initialForm);
    setEditingTask(null);
  };
 const handleGroupSubmit = async (e) => {
    e.preventDefault();
    if (!groupForm.name.trim()) return;

    if (editingGroup) {
      await updateSpaceGroup(editingGroup.id, spaceId, editingGroup.name, groupForm);
    } else {
      await createSpaceGroup(spaceId, groupForm);
    }
    setGroupForm({ name: '', notes: '', priority: 5, parentId: null });
    setEditingGroup(null);
  };
  const handleEditTask = (task) => {
    setEditingTask(task);
    setForm({ ...task });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({ name: group.name, notes: group.notes || '', priority: group.priority || 5, parentId: group.parentId });
  };

// Update renderStars colors
const renderStars = (rating, isForm = false, onRatingChange = null) => {
  return Array.from({ length: 10 }, (_, i) => i + 1).map((i) => (
    <span
      key={i}
      style={{ 
        cursor: 'pointer', 
        color: i <= (hoverRating || rating) ? '#D90429' : '#444', // 🌟 Dark gray for unselected
        fontSize: '1rem' 
      }}
      // ... rest of star logic
    >★</span>
  ));
};
  // 🌟 Filtering Logic (Replicated from Main.js)
const filteredTasks = tasks
  .filter(t => {
    // 1. Basic Safety & Search
    const title = t.title || ""; 
    const notes = t.notes || ""; 
    const searchMatch = title.toLowerCase().includes(search.toLowerCase()) || 
                        notes.toLowerCase().includes(search.toLowerCase());
    
    // 2. Updated Status Filter (Matches Kanban columns)
    // Values expected: 'all', 'todo', 'progress', 'done'
    const statusMatch = statusFilter === 'all' || (t.status || 'todo') === statusFilter;
    
    // 3. Group & Ease Filter
    const taskGroup = (t.group || "all").toLowerCase();
    const activeFilter = filter.toLowerCase();
    const groupMatch = activeFilter === 'all' || taskGroup === activeFilter;
    const easeMatch = easeFilter === 'all' || t.difficulty === easeFilter;
    
    return searchMatch && statusMatch && groupMatch && easeMatch;
  })
  .sort((a, b) => {
    // Helper to normalize Firestore Timestamps or Strings
    const getDate = (val) => {
      if (!val) return null;
      if (val.toDate) return val.toDate(); 
      return new Date(val); 
    };

    if (sortBy === 'priority-desc') return (b.importance || 0) - (a.importance || 0);

    if (sortBy === 'due-soon') {
      const dateA = getDate(a.dueDate) || new Date('9999-12-31');
      const dateB = getDate(b.dueDate) || new Date('9999-12-31');
      return dateA - dateB;
    }

    // Default: Newest Created First
    const createdA = getDate(a.createdAt) || new Date(0);
    const createdB = getDate(b.createdAt) || new Date(0);
    return createdB - createdA;
  });
 return (
    <div className="spaces-wrapper" style={{ display: 'flex', minHeight: '100vh' }}>
      {/* --- Sidebar --- */}
     <aside style={{ width: '300px', borderRight: '2px solid #333', padding: '20px', background: '#1a1a1a' }}>
        <div className="view-switch-container">
          <button className="view-switch-btn active">MANAGE</button>
          <button className="view-switch-btn" onClick={() => navigate(`/${username}/spaces/${spaceId}/kanban`)}>KANBAN</button>
        </div>

        <h2 style={{ fontFamily: 'Space Grotesk', fontSize: '1.5rem', marginBottom: '20px' }}>{spaceData?.name}</h2>
        
        <div className="group-section">
          <h3>TEAM GROUPS</h3>
          <button onClick={() => { setFilter('all'); setForm(p => ({...p, group: ''})); }} className="btn-action" style={{ width: '100%', marginBottom: '10px', background: filter === 'all' ? '#000' : '#D90429' }}>ALL BACKLOG</button>
          
          <div className="groups-tree">
             {buildGroupTree(groups).map(g => (
               <div key={g.id} className="group-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' }}>
                 <button 
                  onClick={() => { setFilter(g.name); setForm(p => ({...p, group: g.name})); }}
                  style={{ background: 'none', border: 'none', fontWeight: filter === g.name ? '800' : '400', cursor: 'pointer', color: filter === g.name ? '#D90429' : '#000' }}
                 ># {g.name}</button>
                 <div style={{ fontSize: '0.8rem' }}>
                   <span onClick={() => handleEditGroup(g)} style={{ cursor: 'pointer', marginRight: '8px' }}>✏️</span>
                   <span onClick={() => deleteSpaceGroupAndTasks(g.id, spaceId, g.name)} style={{ cursor: 'pointer', color: 'red' }}>🗑️</span>
                 </div>
               </div>
             ))}
          </div>

          <form onSubmit={handleGroupSubmit} style={{ marginTop: '20px' }}>
            <input className="brutalist-input" style={{ width: '100%' }} placeholder={editingGroup ? "RENAME GROUP" : "+ NEW GROUP"} value={groupForm.name} onChange={e => setGroupForm({...groupForm, name: e.target.value})} />
            <button type="submit" className="btn-action" style={{ width: '100%', marginTop: '5px' }}>{editingGroup ? 'UPDATE' : 'CREATE'}</button>
          </form>
        </div>
      </aside>

      {/* --- Main Area --- */}
      <main style={{ flex: 1, padding: '40px' }}>
        <form onSubmit={handleFormSubmit} className="member-management-box">
          <input className="brutalist-input" style={{ width: '100%', fontSize: '1.8rem', fontWeight: '800' }} placeholder="OBJECTIVE TITLE" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
          <textarea className="brutalist-input" style={{ width: '100%', marginTop: '10px', minHeight: '60px' }} placeholder="ADD NOTES..." value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
          
          <div style={{ display: 'flex', gap: '15px', marginTop: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div>{renderStars(form.importance, true, (v) => setForm({...form, importance: v}))}</div>
            <select className="brutalist-input" value={form.difficulty} onChange={e => setForm({...form, difficulty: e.target.value})}>
              <option value="">DIFFICULTY</option>
              <option value="Easy">EASY</option>
              <option value="Medium">MEDIUM</option>
              <option value="Hard">HARD</option>
            </select>
            <input type="datetime-local" className="brutalist-input" value={form.dueDate} onChange={e => setForm({...form, dueDate: e.target.value})} />
            <select className="brutalist-input" value={form.assignedToEmail} onChange={e => setForm({...form, assignedToEmail: e.target.value})}>
              <option value="">ASSIGN TO...</option>
              {spaceData?.memberEmails?.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            {/* 🌟 New Group Dropdown */}
<div className="option-group">
  <select 
    className="brutalist-input" 
    value={form.group} 
    onChange={(e) => setForm({ ...form, group: e.target.value })}
  >
    <option value="">NO GROUP (BACKLOG)</option>
    {/* Map through existing space groups */}
    {groups.map((g) => (
      <option key={g.id} value={g.name}>
        {g.name.toUpperCase()}
      </option>
    ))}
  </select>
</div>
            <button type="submit" className="btn-action">{editingTask ? 'SAVE CHANGES' : 'CREATE TASK'}</button>
          </div>
        </form>

        {/* --- Advanced Filters --- */}
        <div style={{ display: 'flex', gap: '15px', margin: '30px 0' }}>
          <input className="brutalist-input" placeholder="SEARCH TASKS..." value={search} onChange={e => setSearch(e.target.value)} />
         <select 
  className="brutalist-input" 
  value={statusFilter} 
  onChange={(e) => setStatusFilter(e.target.value)}
>
  <option value="all">ALL STATUS</option>
  <option value="todo">TO DO</option>
  <option value="progress">IN PROGRESS</option>
  <option value="done">DONE</option>
</select>
          <select className="brutalist-input" value={easeFilter} onChange={e => setEaseFilter(e.target.value)}>
            <option value="all">ALL DIFFICULTY</option>
            <option value="Easy">EASY</option>
            <option value="Medium">MEDIUM</option>
            <option value="Hard">HARD</option>
          </select>
          <select className="brutalist-input" value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="newest">NEWEST</option>
            <option value="priority-desc">PRIORITY</option>
            <option value="due-soon">DUE DATE</option>
          </select>
        </div>

        {/* --- Task List --- */}
        <div className="space-tasks-list">
          {filteredTasks.map(task => (
            <div key={task.id} className="space-task-card" style={{ borderLeft: task.importance > 7 ? '10px solid #D90429' : '3px solid black' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '1.2rem' }}>{task.title}</h4>
                  <p style={{ margin: '5px 0', fontSize: '0.85rem', color: '#666' }}>{task.notes}</p>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <span className="member-tag">#{task.group || 'Backlog'}</span>
                    {/* 🌟 Safe Difficulty Display */}
<span style={{ fontSize: '0.7rem', color: '#D90429', fontWeight: 800 }}>
  {(task.difficulty || "NONE").toUpperCase()}
</span>

{/* 🌟 Safe Status Display */}
<div style={{ fontSize: '0.8rem', fontWeight: '800', color: task.status === 'done' ? 'green' : '#000' }}>
  {(task.status || "TODO").toUpperCase()}
</div>
                    <span style={{ fontSize: '0.7rem', color: '#999' }}>{task.dueDate ? new Date(task.dueDate).toLocaleString() : ''}</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {/* 🌟 Safe Assigned User Display */}
<div style={{ fontSize: '0.8rem', fontWeight: '800' }}>
  {task.assignedToEmail === user.email 
    ? 'YOU' 
    : (task.assignedToEmail || "UNASSIGNED").split('@')[0].toUpperCase()}
</div>
                  <div style={{ marginTop: '10px' }}>
                    <button onClick={() => handleEditTask(task)} style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '10px' }}>✏️</button>
                    <button onClick={() => deleteSpaceTask(task.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'red' }}>🗑️</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export default SpaceMain;
